import { gql } from '@apollo/client/core';

export const CameraDeviceGatewayFieldsFragment = gql`
  fragment CameraDeviceGatewayFieldsFragment on qx_VideoSource {
    deviceID
    gatewayID
    deviceConnectionStatus
    gatewaySyncStatus
    gatewayConnectionStatus
    gatewayPlaceID
  }
`;

export const CameraSearchFragment = gql`
  fragment CameraSearchFragment on qx_VideoSource {
    id
    enabled
    name
    state {
      videoSourceID
      lastMotionAt
    }
    profileMSupported
    metadataEnabled
    place {
      id
      name
      hierarchy {
        name
      }
      toHomePlaces {
        customerPlaceID
        integratorPlaceID
      }
    }
    scheduleID
    ...CameraDeviceGatewayFieldsFragment
	# needed to show audio on camera icon
    audioSources {
      audioSourceID
      audioSource {
        id
        enabled
      }
    }
    device {
      id
      make
      model
    # could be bodyWorn camera
      kind
      serial
      firmware
      deviceAddress
      deviceMac
      port
      tls
      enabled
      connectionStatus
      gateway {
        id
        type
        name
        connectionStatus
        syncStatus
      }
    }
    mediaProfiles {
      id
      refID
      streamEnabled
      streamStatus
      deletedAt
    }
    cloudStorageDays
    cloudStorageTimelapseDays
    timelapseInterval
  }
  ${CameraDeviceGatewayFieldsFragment}
`;

const CameraFieldsFragment = gql`
  fragment CameraFieldsFragment on qx_VideoSource {
    id
    name
    description
    enabled
    cloudStorageDays
    cloudStorageTimelapseDays
    snapshotMediaProfileID
    timelapseInterval
    state {
      videoSourceID
      lastMotionAt
    }
    mountPosition
    profileMSupported
    metadataEnabled
	# needed to show audio on camera icon
    audioSources {
      audioSourceID
      audioSource {
        id
        enabled
      }
    }
    schedule {
      id
      blocks {
        id
        date
        duration
        weekdays
      }
    }
    mountPosition
    schedule {
      id
      blocks {
        id
        date
        duration
        weekdays
      }
    }
    place {
      id
      name
      hierarchy {
        name
      }
      toHomePlaces {
        customerPlaceID
        integratorPlaceID
      }
    }
    ...CameraDeviceGatewayFieldsFragment
    deviceID
    device {
      id
      name
      profileMSupported
      oldestMedia {
        type
        date
      }
      # These audioSources are for the CAMERA_VIEW_CONFIG_ROUTE.  It is all audioSources on the cameara's device.
      audioSources(order_by: {name: asc }) {
        id
        name
        refID
        enabled
        mediaProfiles {
          id
          audioEncoderSettings
          videoSourceID
        }
      }
      gatewayID
      gateway {
        id
        name
        name
        connectionStatus
        syncStatus
        type
        nics
        place {
          id
          name
          timezone
          timezone
          type
          path
        }
      }
      deviceAnalyticSources {
        id
        analyticSource {
          id
          comment
          value
        }
      }
      port
      tls
      make
      model
      deviceAddress
      deviceMac
      deviceUsername
      devicePassword
      serial
      kind
      firmware
      enabled
      connectionStatus
      createdAt
    }
    mediaProfiles(order_by: [{ refID: asc }], where: { deletedAt: { _is_null: true } }) {
      id
      refID
      videoSourceID
      snapshotUrl
      videoEncoderSettings
      streamEnabled
      streamStatus
      streamFile
      # audio sources here are used on CAMERA_EDIT_ROUTE.  These media profiles are limited to only those on the current video source
      audioSource {
        id
        refID
        enabled
      }
      audioEncoderSettings
      audioCodec
    }
    fps
    imageSize
    imagingSettings
    imagingOptions
  }
  ${CameraDeviceGatewayFieldsFragment}
`;

// same as `SingleCameraFragment`, but without snapshots
const SingleCameraSubscriptionFragment = gql`
  fragment SingleCameraSubscriptionFragment on qx_VideoSource {
    ...CameraFieldsFragment
  }
  ${CameraFieldsFragment}
`;

export const SnapshotsFieldFragment = gql`
  fragment SnapshotsFieldFragment on qx_VideoSource {
    snapshots: latestSnapshots {
      id
      base64
      signedUrl
      aspectRatio
    }
  }
`;

const SingleCameraFragment = gql`
  fragment SingleCameraFragment on qx_VideoSource {
    ...CameraFieldsFragment
    ...SnapshotsFieldFragment
  }
  ${CameraFieldsFragment}
  ${SnapshotsFieldFragment}
`;

export const LiveScrollerCameraResolveFragment = gql`
  fragment LiveScrollerCameraResolveFragment on qx_VideoSource {
    id
    name
  }
`;

export const MediaProfileFormFragment = gql`
  fragment MediaProfileFormFragment on qx_MediaProfile {
    id
    deviceID
    refID
    streamEnabled
    warped
    videoEncoderOptions
    videoEncoderSettings
  }
`;

export const GQL_CREATE_CAMERA_DEVICE = gql`
  mutation GQL_CREATE_CAMERA_DEVICE($device: CreateDeviceInput!) {
    createDevices(objects: [$device]) {
      id
      videoSources {
        ...SingleCameraFragment
      }
    }
  }
  ${SingleCameraFragment}
`;

export const GQL_GET_CAMERA_BY_ID = gql`
  query GQL_GET_CAMERA_BY_ID($id: bigint!) {
    videoSource(id: $id) {
      ...SingleCameraFragment
    }
  }
  ${SingleCameraFragment}
`;

export const GQL_GET_CAMERAS_BY_IDS = gql`
  query GQL_GET_CAMERAS_BY_IDS($ids: [bigint!]!) {
    videoSources(where: { id: { _in: $ids } }) {
      ...LiveScrollerCameraResolveFragment
    }
  }
  ${LiveScrollerCameraResolveFragment}
`;

export const GQL_GET_AI_TOOL_RESULT_CAMERAS_BY_IDS = gql`
  query GQL_GET_AI_TOOL_RESULT_CAMERAS_BY_IDS($ids: [bigint!]!) {
    videoSources(where: { id: { _in: $ids } }) {
      ...CameraSearchFragment
      ...SnapshotsFieldFragment
    }
  }
  ${CameraSearchFragment}
  ${SnapshotsFieldFragment}
`;

export const GQL_SUB_CAMERA_BY_ID = gql`
  subscription GQL_SUB_CAMERA_BY_ID($id: bigint!) {
    videoSource(id: $id) {
      ...SingleCameraSubscriptionFragment
    }
  }
  ${SingleCameraSubscriptionFragment}
`;

export const GQL_GET_CAMERA_MEDIA_PROFILE_FORM = gql`
  query GQL_GET_CAMERA_MEDIA_PROFILE_FORM($id: bigint!) {
    mediaProfile(id: $id) {
      ...MediaProfileFormFragment
    }
  }
  ${MediaProfileFormFragment}
`;

export const GQL_UPDATE_VIDEO_SOURCE_BY_ID = gql`
  mutation GQL_UPDATE_VIDEO_SOURCE_BY_ID($id: bigint!, $data: qx_VideoSource_set_input!) {
    updateVideoSource(pk_columns: { id: $id }, _set: $data) {
      id
    }
  }
`;

export const GQL_DELETE_CAMERA_DEVICE_BY_ID = gql`
  mutation GQL_DELETE_CAMERA_DEVICE_BY_ID($id: String!) {
    deleteDeviceVideoData(deviceID: $id) {
      status
    }
  }
`;

export const GQL_GET_CAMERAS_BY_DEVICE_ID = gql`
  query GQL_GET_CAMERAS_BY_DEVICE_ID($id: String!) {
    videoSources(where: { device: { id: { _eq: $id } } }) {
      ...SingleCameraFragment
    }
  }
  ${SingleCameraFragment}
`;

export const GQL_CAMERA_SEARCH = gql`
  query GQL_CAMERA_SEARCH(
    $where: qx_VideoSource_bool_exp
    $limit: Int
    $offset: Int
    $orderBy: [qx_VideoSource_order_by!]
  ) {
    videoSources(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...CameraSearchFragment
      snapshots: latestSnapshots {
        id
        base64
        signedUrl
        aspectRatio
      }
    }
    videoSourceAggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
  ${CameraSearchFragment}
`;

export const GQL_CAMERA_SEARCH_SUBSCRIPTION = gql`
  subscription GQL_CAMERA_SEARCH_SUBSCRIPTION(
    $where: qx_VideoSource_bool_exp
    $limit: Int
    $offset: Int
    $orderBy: [qx_VideoSource_order_by!]
  ) {
    videoSources(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
      ...CameraSearchFragment
    }
  }
  ${CameraSearchFragment}
`;

export const DeviceVideoSourceFormFragment = gql`
  fragment DeviceVideoSourceFormFragment on qx_VideoSource {
    id
    refID
    name
    description
    enabled
    cloudStorageDays
    cloudStorageTimelapseDays
    timelapseInterval
    mountPosition
    placeID
    place {
      id
      name
    }
    mediaProfiles(order_by: [{ refID: asc }]) {
      id
      refID
      deviceID
      warped
      streamEnabled
      streamStatus
      videoEncoderOptions
      videoEncoderSettings
    }
  }
`;

export const GQL_SUB_CAMERA_DEVICE_FORM_CREATE = gql`
  subscription GQL_SUB_CAMERA_DEVICE_FORM_CREATE($id: String!) {
    videoSources(where: { deviceID: { _eq: $id } }) {
      ...DeviceVideoSourceFormFragment
    }
  }
  ${DeviceVideoSourceFormFragment}
`;
